import React, { useState } from 'react'
import styled from 'styled-components'
import {
  compose, space, color, typography, SpaceProps
} from 'styled-system'
import { checkboxes, texts, useDidMountEffect } from '@zuto/utilities'
import { TextProps, Texts } from '../Form/text-utils'

type CheckboxVariant = 'standard' | 'small'
type CheckboxHTMLProps = React.InputHTMLAttributes<HTMLInputElement>

interface StyledCheckboxProps extends CheckboxHTMLProps {
  $variant: CheckboxVariant
}

export interface CheckboxProps extends CheckboxHTMLProps, SpaceProps {
  /**
   * Changes the preset size of the Checkbox
   */
  variant?: CheckboxVariant
  /**
   * Enables the Checkbox to be selected by default
   */
  defaultState?: boolean
  /** A callback function that is triggered whenever the internal state is changed */
  valueChanged?: (_?: boolean | 'indeterminate' | (string | number)[]) => void
}

const StyledFormLabel = styled.label<React.LabelHTMLAttributes<HTMLLabelElement> & TextProps>`
  ${texts}
  ${compose(space, color, typography)}
  display: flex;
  align-items: center;
`

const StyledCheckbox = styled.input<StyledCheckboxProps>`
  ${checkboxes}
  ${compose(space, color, typography)}
`

const Checkbox = ({
  children,
  variant = 'standard',
  defaultState = false,
  value,
  valueChanged,
  className,
  ...props
}: CheckboxProps) => {
  const [checked, setChecked] = useState(defaultState)

  useDidMountEffect(() => {
    if (valueChanged) {
      valueChanged(checked)
    }
  }, [checked])

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (props.disabled) return
    props.onChange?.(event)
    setChecked(!checked)
  }

  const labelVariant = `checkbox.${variant}` as Texts

  return (
    <StyledFormLabel className={className} $variant={labelVariant} data-testid="input-label">
      {/* aria-checked, and when disabled, aria-disabled and no pointer events (so no hover styles) */}
      <StyledCheckbox
        type="checkbox"
        aria-disabled={props.disabled || undefined}
        $variant={variant}
        {...props}
        style={props.disabled ? { pointerEvents: 'none', ...props.style } : props.style}
        value={value}
        checked={checked}
        aria-checked={checked}
        onChange={onChange}
      />
      {children}
    </StyledFormLabel>
  )
}

export default Checkbox
